import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Filter, Grid, List, X, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { ProductCard } from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { fetchProducts, fetchCategories } from '../api/shopify';
import { SEO, pageSEO, categorySEO } from '../components/SEO';
import { getOptimizedImageUrl } from '../utils/image';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import { Checkbox } from '../components/ui/checkbox';
import { Slider } from '../components/ui/slider';
import { purposes } from '../data/purposes';

// Define parent-child category relationships
// Subcategories will be nested under their parent in the sidebar
// Add new subcategory handles here as you create them in Shopify
const CATEGORY_HIERARCHY = {
  'gemstones': {
    name: 'Gemstones',
    // Add more gemstone subcategories as you create them in Shopify
    subcategories: ['ruby-gemstone', 'moonga-gemstone', 'gomeda-gemstone']
  },
  // Uncomment and add handles when you create these subcategories in Shopify:
  // 'rudraksha': {
  //   name: 'Rudraksha',
  //   subcategories: ['1-mukhi-rudraksha', '2-mukhi-rudraksha', '3-mukhi-rudraksha']
  // },
  // 'yantras': {
  //   name: 'Yantras', 
  //   subcategories: ['shree-yantra', 'kuber-yantra', 'navgraha-yantra']
  // }
};

// Get all subcategory handles for filtering
const ALL_SUBCATEGORIES = Object.values(CATEGORY_HIERARCHY).flatMap(h => h.subcategories);

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false); // Start with false, show content immediately
  const [initialLoad, setInitialLoad] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('popular');
  const [visibleCount, setVisibleCount] = useState(24); // incremental render batch
  const [priceRange, setPriceRange] = useState([0, 20000]);
  const [selectedCategories, setSelectedCategories] = useState(
    searchParams.get('category') ? [searchParams.get('category')] : []
  );
  
  // Auto-expand parent categories that have selected subcategories or are selected themselves
  const [expandedParents, setExpandedParents] = useState(() => {
    const initial = {};
    const category = searchParams.get('category');
    if (category) {
      // If the selected category is a parent in hierarchy, expand it
      if (CATEGORY_HIERARCHY[category]) {
        initial[category] = true;
      }
      // If the selected category is a subcategory, expand its parent
      Object.entries(CATEGORY_HIERARCHY).forEach(([parentHandle, config]) => {
        if (config.subcategories.includes(category)) {
          initial[parentHandle] = true;
        }
      });
    }
    // Also expand gemstones by default since it has subcategories
    initial['gemstones'] = true;
    return initial;
  });
  const searchQuery = searchParams.get('search') || '';
  // "Shop by Purpose" deep links: /products?purpose=money -> search by that purpose's keywords
  const purposeId = searchParams.get('purpose') || '';
  const activePurpose = purposes.find((p) => p.id === purposeId);
  const effectiveSearch = searchQuery || (activePurpose ? activePurpose.searchQuery : '');

  // Fetch products and categories - fast from cache
  useEffect(() => {
    const loadData = async () => {
      // Only show loading spinner on subsequent loads, not initial
      if (!initialLoad) {
        setLoading(true);
      }
      try {
        // Build API URL with params
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        let apiUrl = `${API_URL}/api/products`;
        const params = new URLSearchParams();
        params.append('lite', 'true');
        
        if (selectedCategories.length === 1) {
          params.append('category', selectedCategories[0]);
        }
        if (effectiveSearch) {
          params.append('search', effectiveSearch);
        }
        
        apiUrl += `?${params.toString()}`;
        
        const [productsResponse, categoriesData] = await Promise.all([
          fetch(apiUrl).then(r => r.json()),
          fetchCategories(),
        ]);
        setProducts(productsResponse);
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    };
    loadData();
  }, [selectedCategories, effectiveSearch, initialLoad]);

  const filteredProducts = useMemo(() => {
    let filtered = [...products];

    // Price filter only (category filter is handled by API now)
    filtered = filtered.filter(
      (p) => p.price >= priceRange[0] && p.price <= priceRange[1]
    );

    // Sort
    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'rating':
        filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      case 'newest':
        filtered.reverse();
        break;
      default:
        filtered.sort((a, b) => (b.reviews || 0) - (a.reviews || 0));
    }

    return filtered;
  }, [products, priceRange, sortBy]);

  // Reset how many products are rendered whenever the result set changes
  useEffect(() => {
    setVisibleCount(24);
  }, [filteredProducts]);

  const visibleProducts = filteredProducts.slice(0, visibleCount);

  const toggleCategory = (categoryHandle) => {
    const newSelection = selectedCategories.includes(categoryHandle)
      ? selectedCategories.filter((c) => c !== categoryHandle)
      : [categoryHandle]; // Only allow single category selection for API-based filtering

    setSelectedCategories(newSelection);
    if (newSelection.length > 0) {
      setSearchParams({ category: newSelection[0] });
    } else {
      setSearchParams({});
    }
  };

  const clearFilters = () => {
    setSelectedCategories([]);
    setPriceRange([0, 20000]);
    setSearchParams({});
  };

  // Toggle parent category expansion
  const toggleParentExpand = (parentHandle) => {
    setExpandedParents(prev => ({
      ...prev,
      [parentHandle]: !prev[parentHandle]
    }));
  };

  // Organize categories into hierarchy
  const organizedCategories = useMemo(() => {
    const result = [];
    const subcategoryHandles = new Set(ALL_SUBCATEGORIES);

    // First, process parent categories with their subcategories
    Object.entries(CATEGORY_HIERARCHY).forEach(([parentHandle, config]) => {
      const parentCat = categories.find(c => c.handle === parentHandle);
      if (parentCat) {
        const subcats = config.subcategories
          .map(subHandle => categories.find(c => c.handle === subHandle))
          .filter(Boolean);
        
        result.push({
          ...parentCat,
          subcategories: subcats,
          isParent: true
        });
      }
    });

    // Then add remaining categories that are NOT subcategories and NOT "home page"
    categories.forEach(cat => {
      const isSubcategory = subcategoryHandles.has(cat.handle);
      const isParent = CATEGORY_HIERARCHY.hasOwnProperty(cat.handle);
      const isHomePage = cat.name.toLowerCase() === 'home page' || cat.handle === 'frontpage';
      
      // Only add if it's not a subcategory, not already added as parent, and not home page
      if (!isSubcategory && !isParent && !isHomePage) {
        result.push({ ...cat, subcategories: [], isParent: false });
      }
    });

    return result;
  }, [categories]);

  const FilterSidebar = () => (
    <div className="space-y-8">
      {/* Categories with Hierarchy */}
      <div>
        <h3 className="font-serif text-lg font-semibold text-foreground mb-4">
          Categories
        </h3>
        <div className="space-y-2">
          {organizedCategories.map((category) => (
            <div key={category.handle || category.id}>
              {/* Parent Category or Standalone Category */}
              <div className="flex items-center gap-2">
                {category.subcategories?.length > 0 && (
                  <button
                    onClick={() => toggleParentExpand(category.handle)}
                    className="p-1 hover:bg-amber-50 rounded transition-colors"
                  >
                    {expandedParents[category.handle] ? (
                      <ChevronDown className="w-4 h-4 text-amber-600" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                )}
                <Checkbox
                  id={category.handle || category.id}
                  checked={selectedCategories.includes(category.handle)}
                  onCheckedChange={() => toggleCategory(category.handle)}
                  data-testid={`filter-category-${category.handle}`}
                />
                <label
                  htmlFor={category.handle || category.id}
                  className={`text-sm cursor-pointer flex-1 ${
                    category.subcategories?.length > 0 ? 'font-medium text-foreground' : 'text-foreground'
                  }`}
                  onClick={() => {
                    if (category.subcategories?.length > 0) {
                      toggleParentExpand(category.handle);
                    }
                  }}
                >
                  {category.name}
                  {category.subcategories?.length > 0 && (
                    <span className="text-xs text-gray-400 ml-1">({category.subcategories.length})</span>
                  )}
                </label>
              </div>

              {/* Subcategories (Nested) */}
              <AnimatePresence>
                {category.subcategories?.length > 0 && expandedParents[category.handle] && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="ml-8 mt-2 space-y-2 border-l-2 border-amber-100 pl-3">
                      {category.subcategories.map((subcat) => (
                        <div key={subcat.handle} className="flex items-center gap-2">
                          <Checkbox
                            id={subcat.handle}
                            checked={selectedCategories.includes(subcat.handle)}
                            onCheckedChange={() => toggleCategory(subcat.handle)}
                            data-testid={`filter-category-${subcat.handle}`}
                            className="h-4 w-4"
                          />
                          <label
                            htmlFor={subcat.handle}
                            className="text-sm text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                          >
                            {subcat.name}
                          </label>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>

      {/* Price Range */}
      <div>
        <h3 className="font-serif text-lg font-semibold text-foreground mb-4">
          Price Range
        </h3>
        <Slider
          value={priceRange}
          onValueChange={setPriceRange}
          max={20000}
          step={100}
          className="mb-4"
          data-testid="price-slider"
        />
        <div className="flex justify-between text-sm text-muted-foreground">
          <span>₹{priceRange[0]}</span>
          <span>₹{priceRange[1]}</span>
        </div>
      </div>

      {/* Clear Filters */}
      <Button
        variant="outline"
        className="w-full border-amber-200 hover:border-gold hover:text-gold"
        onClick={clearFilters}
        data-testid="clear-filters"
      >
        Clear All Filters
      </Button>
    </div>
  );

  // Dynamic SEO based on category/search - with remedy-focused content
  const getSEOProps = () => {
    const category = searchParams.get('category');
    const search = searchParams.get('search');
    
    if (search) {
      return {
        title: `${search} Remedies & Products | Astro Vastu Remedies`,
        description: `Find ${search} remedies and spiritual products at Astro Vastu Remedies. Shop authentic, energized products for healing and success.`,
        keywords: `${search}, ${search} remedy, ${search} products, spiritual ${search}`,
        noindex: true, // Don't index search results
      };
    }
    
    if (category) {
      // Check if we have specific SEO for this category
      if (categorySEO[category]) {
        return {
          ...categorySEO[category],
          url: `https://astrovasturemedies.in/products?category=${category}`,
        };
      }
      
      // Default category SEO with remedy focus
      const categoryName = categories.find(c => c.handle === category)?.name || category;
      return {
        title: `${categoryName} Remedies | Buy Online | Astro Vastu Remedies`,
        description: `Shop authentic ${categoryName.toLowerCase()} for astrology remedies & spiritual healing. Lab-certified and energized by expert astrologers. Fast delivery across India.`,
        keywords: `${categoryName.toLowerCase()}, ${categoryName.toLowerCase()} remedy, buy ${categoryName.toLowerCase()}, ${categoryName.toLowerCase()} benefits`,
        url: `https://astrovasturemedies.in/products?category=${category}`,
      };
    }
    
    return pageSEO.products;
  };

  if (loading && products.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-cream to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-gold mx-auto mb-4" />
          <p className="text-muted-foreground">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8 lg:py-12 bg-gradient-to-b from-cream to-white min-h-screen" data-testid="products-page">
      <SEO {...getSEOProps()} />
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3">
            <h1 className="font-serif text-3xl lg:text-4xl font-bold text-foreground mb-2">
              {activePurpose ? (
                <>For <span className="text-gold">{activePurpose.name}</span></>
              ) : (
                <>All <span className="text-gold">Products</span></>
              )}
            </h1>
            {loading && <Loader2 className="w-5 h-5 animate-spin text-gold" />}
          </div>
          <p className="text-muted-foreground">
            {activePurpose ? `${filteredProducts.length} products for ${activePurpose.description.toLowerCase()}` : `${filteredProducts.length} products found`}
          </p>
        </motion.div>

        {/* Active purpose filter chip */}
        {activePurpose && (
          <div className="flex flex-wrap gap-2 mb-6">
            <Button
              variant="outline"
              size="sm"
              className="border-gold text-gold hover:bg-gold/10 rounded-full"
              onClick={() => setSearchParams({})}
              data-testid="clear-purpose-filter"
            >
              Purpose: {activePurpose.name}
              <X className="w-3 h-3 ml-2" />
            </Button>
          </div>
        )}

        {/* Active Filters */}
        {selectedCategories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {selectedCategories.map((catHandle) => {
              const cat = categories.find((c) => c.handle === catHandle);
              return (
                <Button
                  key={catHandle}
                  variant="outline"
                  size="sm"
                  className="border-gold text-gold hover:bg-gold/10 rounded-full"
                  onClick={() => toggleCategory(catHandle)}
                >
                  {cat?.name || catHandle}
                  <X className="w-3 h-3 ml-2" />
                </Button>
              );
            })}
          </div>
        )}

        {/* Subcategories Section - Show when parent category is selected */}
        {selectedCategories.length === 1 && CATEGORY_HIERARCHY[selectedCategories[0]] && (
          <div className="mb-8">
            <h2 className="font-serif text-xl font-semibold text-foreground mb-4">
              Browse {categories.find(c => c.handle === selectedCategories[0])?.name || selectedCategories[0]} Categories
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {CATEGORY_HIERARCHY[selectedCategories[0]].subcategories.map((subHandle) => {
                const subCat = categories.find(c => c.handle === subHandle);
                if (!subCat) return null;
                
                return (
                  <motion.div
                    key={subHandle}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    whileHover={{ scale: 1.02 }}
                    className={`bg-white rounded-xl p-4 border cursor-pointer transition-all ${
                      selectedCategories.includes(subHandle) 
                        ? 'border-gold ring-2 ring-gold/20' 
                        : 'border-amber-100 hover:border-gold hover:shadow-md'
                    }`}
                    onClick={() => {
                      setSelectedCategories([subHandle]);
                      setSearchParams({ category: subHandle });
                    }}
                  >
                    {subCat.image ? (
                      <img 
                        src={getOptimizedImageUrl(subCat.image, 400)} 
                        alt={subCat.name}
                        loading="lazy"
                        decoding="async"
                        className="w-full aspect-square object-cover rounded-lg mb-3"
                      />
                    ) : (
                      <div className="w-full aspect-square bg-gradient-to-br from-amber-100 to-amber-200 rounded-lg mb-3 flex items-center justify-center">
                        <span className="text-3xl">💎</span>
                      </div>
                    )}
                    <h3 className="font-medium text-foreground text-sm text-center">
                      {subCat.name}
                    </h3>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex gap-8">
          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="bg-white rounded-xl p-6 border border-amber-100 shadow-sm">
              <FilterSidebar />
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              {/* Mobile Filter Button */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button
                    variant="outline"
                    className="lg:hidden border-amber-200"
                    data-testid="mobile-filter-btn"
                  >
                    <Filter className="w-4 h-4 mr-2" />
                    Filters
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="bg-white border-amber-100 w-80">
                  <SheetHeader>
                    <SheetTitle className="font-serif text-gold">Filters</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6">
                    <FilterSidebar />
                  </div>
                </SheetContent>
              </Sheet>

              <div className="flex items-center gap-4 ml-auto">
                {/* Sort */}
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-40 bg-white border-amber-200" data-testid="sort-select">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-amber-100">
                    <SelectItem value="popular">Most Popular</SelectItem>
                    <SelectItem value="newest">Newest</SelectItem>
                    <SelectItem value="price-low">Price: Low to High</SelectItem>
                    <SelectItem value="price-high">Price: High to Low</SelectItem>
                    <SelectItem value="rating">Highest Rated</SelectItem>
                  </SelectContent>
                </Select>

                {/* View Mode */}
                <div className="hidden sm:flex items-center gap-1 bg-white rounded-lg p-1 border border-amber-200">
                  <Button
                    variant={viewMode === 'grid' ? 'default' : 'ghost'}
                    size="icon"
                    className={viewMode === 'grid' ? 'bg-gold text-white' : ''}
                    onClick={() => setViewMode('grid')}
                    data-testid="view-grid"
                  >
                    <Grid className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'default' : 'ghost'}
                    size="icon"
                    className={viewMode === 'list' ? 'bg-gold text-white' : ''}
                    onClick={() => setViewMode('list')}
                    data-testid="view-list"
                  >
                    <List className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Products Grid */}
            {filteredProducts.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-amber-100">
                <p className="text-muted-foreground text-lg mb-4">
                  No products found
                </p>
                <Button
                  onClick={clearFilters}
                  className="gold-gradient text-white"
                >
                  Clear Filters
                </Button>
              </div>
            ) : (
              <div
                className={
                  viewMode === 'grid'
                    ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'
                    : 'space-y-4'
                }
              >
                {visibleProducts.map((product, index) => (
                  <ProductCard key={product.id} product={product} index={index} />
                ))}
              </div>
            )}

            {/* Load More */}
            {visibleCount < filteredProducts.length && (
              <div className="flex justify-center mt-10">
                <Button
                  onClick={() => setVisibleCount((c) => c + 24)}
                  className="gold-gradient text-white px-8 py-6 rounded-full text-base font-semibold shadow-lg hover:opacity-90"
                  data-testid="load-more-products"
                >
                  Load More ({filteredProducts.length - visibleCount} more)
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
